import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ENDPOINTS,
    EquipmentModel,
    ODataServiceKeyModel,
    OnlineOnlyService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EquipmentConfigurationServiceKeyModel } from './equipment-configuration-service-key.model';

@Component({
    selector: 'pace-equipment-configuration-service-key',
    templateUrl: './equipment-configuration-service-key.component.html',
    styleUrls: ['./equipment-configuration-service-key.component.scss'],
    standalone: false,
})
export class EquipmentConfigurationServiceKeyComponent implements OnInit {
    @Input()
    public model: EquipmentConfigurationServiceKeyModel;

    @Output()
    public serviceKeySetChanged = new EventEmitter<void>();

    // form in HTML template
    public formChildren: FormArray;
    public equipmentServiceKeyForm: FormGroup;
    public isLoading = false;
    public constructor(
        public readonly modalInstance: BsModalRef,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.equipmentServiceKeyForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    /**
     * Sends post request to get the service key from backend
     */
    public getServiceKey(): void {
        this.isLoading = true;
        this.onlineOnlyService
            .post<ODataServiceKeyModel>(ENDPOINTS.ServiceKey, this.model.getOutModel())
            .pipe(
                map((data: ODataServiceKeyModel) => {
                    this.modalInstance.hide();
                    if (data.ServiceKey) {
                        const serviceKey = EquipmentModel.returnFormattedServiceKey(data.ServiceKey);
                        const formattedServiceKey = `${serviceKey.primaryText}<strong>${serviceKey.emphasizedText}</strong>`;
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.INFO,
                            <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_SERVICE_KEY'),
                            formattedServiceKey,
                        );
                        this.serviceKeySetChanged.emit();
                    } else {
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.WARN,
                            <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_SERVICE_KEY'),
                            <string>(
                                this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_CONFIGURATION_NO_SERVICE_KEY')
                            ),
                        );
                    }
                }),
                catchError((error) => {
                    this.modalInstance.hide();
                    this.log.error('getServiceKey: Error:', error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_SERVICE_KEY_ERROR_NO_TITLE'),
                        `${<string>(
                            this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_SERVICE_KEY_ERROR_NO_MESSAGE')
                        )} ${JSON.stringify(error)}`,
                    );
                    return of(undefined);
                }),
                map(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe();
    }

    public resetRequiredOption(): void {
        this.model.resetRequiredOption();
        this.formChildren.controls.forEach((control) => {
            control.updateValueAndValidity();
        });
        this.changeDetector.detectChanges();
    }
}
